import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { useServerEnv } from '@/hooks/useServerEnv';
import { scaleOrdinal, scaleLinear, scaleThreshold } from '@visx/scale';
import type { ScaleThreshold } from 'd3-scale';
import { Valuation, Axis } from '@/components/DCF';
import { toCurrency, toPercentage } from '@simplywallst/formatting';
import useWindowSize from '@/hooks/useWindowSize';

import Label from './Label';
import { StyledSVG } from './styled';

import {
  keys,
  bandingKeys,
  bandingKeysReversed,
  valuationPaddingBreakPoints,
  axisPaddingBreakPoints,
  breakPointMapper,
  normalise,
} from './utils';

interface Props {
  sharePrice: number;
  authorFairValue: number;
  authorImageUrl: string;
  currencyISO: string;
}

export const ValuationHistogram = ({
  sharePrice,
  currencyISO,
  authorFairValue,
  authorImageUrl,
}: Props) => {
  const chartHeight = 140;
  const barHeight = 48;
  const barPadding = 12;
  const bandingHeight = 128;
  const theme = useTheme();
  const size = useWindowSize();

  const containerRef = useRef<SVGSVGElement>(null);
  const axisRef = useRef<SVGSVGElement>(null);
  const [bound, setBound] = useState<number>(0);

  const data = normalise(authorFairValue, sharePrice);
  const xScale = scaleLinear<number>({
    domain: [-0.1, 1.3],
  });

  const normalisedAuthorFairValue = data[keys[0]];

  // This is to draw yellow band which goes from 20% above to 20% below FAIR value
  const authorFairValueXMin = xScale(
    normalisedAuthorFairValue - normalisedAuthorFairValue * 0.2
  ) as number;
  const authorFairValueXMax = xScale(
    normalisedAuthorFairValue + normalisedAuthorFairValue * 0.2
  ) as number;

  const fairValueX = xScale(data[keys[0]]);
  const sharePriceX = xScale(data[keys[1]]);

  const bandingColors: string[] = [
    theme.color.good,
    theme.color.neutral,
    theme.color.bad,
  ];

  const bandingColor = scaleOrdinal({
    domain: bandingKeys,
    range: bandingColors,
  });

  const bandingScale = scaleOrdinal({
    domain: bandingKeys,
    range: [authorFairValueXMin, authorFairValueXMax, 1],
  });

  const axisScale = scaleOrdinal({
    domain: bandingKeys,
    range: [authorFairValueXMin, fairValueX, authorFairValueXMax],
  });

  const colorThreshold = scaleThreshold({
    domain: [0, authorFairValueXMin, authorFairValueXMax, 1],
    range: ['white', ...bandingColors],
  }) as ScaleThreshold<number, string>;

  const min = Math.min(fairValueX, sharePriceX);
  const diff = Math.abs(fairValueX - sharePriceX);
  const diffPercentage =
    Math.abs(sharePrice - authorFairValue) / authorFairValue;

  const serverEnv = useServerEnv();

  const [valuationPadding, setValuationPadding] = useState<number>(
    serverEnv.isServer
      ? valuationPaddingBreakPoints[breakPointMapper[serverEnv.size]]
      : 0
  );

  const [axisPadding, setAxisPadding] = useState<number>(
    serverEnv.isServer
      ? axisPaddingBreakPoints[breakPointMapper[serverEnv.size]]
      : 0
  );

  useEffect(() => {
    if (size.width !== undefined && size.width < theme.mdMinRaw) {
      valuationPadding !== valuationPaddingBreakPoints.mobile &&
        setValuationPadding(valuationPaddingBreakPoints.mobile);
    } else {
      valuationPadding !== valuationPaddingBreakPoints.desktop &&
        setValuationPadding(valuationPaddingBreakPoints.desktop);
    }
  }, [size, theme.mdMinRaw, valuationPadding]);

  useEffect(() => {
    const { current: container } = containerRef;
    if (container === null) return;
    const containerWidth = container.getBBox().width;
    setBound(containerWidth);
  }, [authorFairValue, sharePrice, size]);

  useEffect(() => {
    const { current: axis } = axisRef;
    if (axis) {
      let axisHeight = axisPadding;
      if (size.width !== undefined) {
        if (
          size.width < theme.mdMinRaw &&
          axisPadding !== axisPaddingBreakPoints.mobile
        ) {
          axisHeight = axisPaddingBreakPoints.mobile;
        }
        if (
          size.width > theme.mdMinRaw &&
          axisPadding !== axisPaddingBreakPoints.desktop
        ) {
          axisHeight = axisPaddingBreakPoints.desktop;
        }
      }
      setAxisPadding(axisHeight);
    }
  }, [
    authorFairValue,
    sharePrice,
    bound,
    axisScale,
    size.width,
    axisPadding,
    theme.mdMinRaw,
  ]);

  return (
    <StyledSVG
      width="100%"
      height={valuationPadding + chartHeight + axisPadding}
      shapeRendering="crispEdges"
    >
      <Valuation
        color={colorThreshold(sharePriceX) || theme.color.charcoal}
        title={sharePrice < authorFairValue ? 'Undervalued' : 'Overvalued'}
        value={toPercentage(diffPercentage, 1)}
        x={min}
      />
      <svg
        width="100%"
        height={chartHeight}
        y={valuationPadding}
        ref={containerRef}
      >
        <rect
          x="0"
          y={barPadding}
          width="100%"
          height={bandingHeight}
          fill={theme.color.bad}
          rx={6}
        />
        <defs>
          <linearGradient id="BarGradient">
            <stop offset="0%" stopColor="rgb(21, 27, 36)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="rgb(21, 27, 36)" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        {bandingKeysReversed.map((key, i) => {
          if (key !== '20% Overvalued')
            return (
              <rect
                key={`banding-${i}`}
                x="0"
                y={barPadding}
                width={`${bandingScale(key) * 100}%`}
                height={bandingHeight}
                fill={bandingColor(key)}
              />
            );
          else return null;
        })}
        <rect
          x="0"
          y={barPadding}
          width="100%"
          height={bandingHeight}
          opacity={0.4}
        />
        <g transform={`translate(0,${barPadding})`}>
          {keys.map((key, i) => {
            const xScaleLinear = xScale(data[key]);
            const xPos = `${Number(xScaleLinear) * 100}%`;
            const yPos = barPadding + i * barHeight + i * barPadding;

            return (
              <g key={`barstack-horizontal-${i}`}>
                <rect
                  x="0"
                  y={yPos}
                  width={xPos}
                  height={barHeight}
                  fill={i === 0 ? theme.color.noir : 'url(#BarGradient)'}
                />
                <line
                  strokeWidth={2}
                  x1={xPos}
                  x2={xPos}
                  y1={yPos}
                  y2={yPos + barHeight}
                  stroke={i === 0 ? theme.color.brand01 : 'white'}
                />
              </g>
            );
          })}
        </g>
        {keys.map((key, i) => {
          const xScaleLinear = xScale(data[key]) as number;
          const xPos = `${Number(xScaleLinear) * 100}%`;
          return (
            <line
              key={`marker-${i}`}
              x1={xPos}
              x2={xPos}
              y1="0"
              y2={barPadding * (i + 2) + (i + 1) * barHeight}
              stroke="white"
              strokeOpacity="0.3"
            />
          );
        })}
        <rect
          x={`${min * 100}%`}
          y="0"
          width={`${diff * 100}%`}
          height="2"
          fill={colorThreshold(sharePriceX)}
        />
        <Label
          bound={bound}
          height={barHeight}
          x={fairValueX}
          y={barPadding * 2}
          value={toCurrency(authorFairValue, 2, currencyISO)}
          authorProfileImage={authorImageUrl}
        />
        <Label
          bound={bound}
          height={barHeight}
          x={sharePriceX}
          y={barPadding * 3 + barHeight}
          label="Current price"
          value={toCurrency(sharePrice, 1, currencyISO)}
        />
      </svg>
      <Axis
        ref={axisRef}
        color={bandingColor}
        keys={bandingKeys}
        xScale={axisScale}
        y={valuationPadding + chartHeight}
      />
    </StyledSVG>
  );
};
